import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';

const MentionInput = ({
  value,
  onChange,
  questions = [], // Array of { id: string, text: string }
  placeholder,
  className,
  disabled,
  name,
  multiline = false, // New prop to enable textarea mode
  rows = 4, // Default rows for textarea
  ...props
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;

    // Find if we're typing after an @ symbol
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');

    if (atIndex !== -1) {
      const searchTerm = textBeforeCursor.substring(atIndex + 1);

      // Only show suggestions if we're directly after @ or typing a search term
      if (searchTerm === '' || /^[a-zA-Z0-9_]*$/.test(searchTerm)) {
        const filtered = questions.filter(q => 
          q.text.toLowerCase().includes(searchTerm.toLowerCase())
        );

        setFilteredQuestions(filtered);
        setShowSuggestions(filtered.length > 0);
        setSelectedIndex(0);
        setMentionStart(atIndex);
        setCursorPosition(cursorPos);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }

    onChange(e);
  };

  const handleSuggestionClick = (question) => {
    const beforeMention = value.substring(0, mentionStart);
    const afterCursor = value.substring(cursorPosition);

    // Insert the placeholder in the correct format: {{question_X_response}}
    const placeholder = `{{${question.text}}}`;
    const newValue = beforeMention + placeholder + afterCursor;

    // Create a synthetic event
    const syntheticEvent = {
      target: {
        name: name,
        value: newValue
      }
    };

    onChange(syntheticEvent);
    setShowSuggestions(false);

    // Focus back to input and set cursor position
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        const newCursorPos = beforeMention.length + placeholder.length;
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredQuestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredQuestions.length - 1
        );
        break;
      case 'Enter':
      case 'Tab':
        e.preventDefault();
        if (filteredQuestions[selectedIndex]) {
          handleSuggestionClick(filteredQuestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  // Determine if we should auto-detect multiline based on className or explicit prop
  const shouldUseTextarea = multiline || (className && className.includes('min-h-'));

  return (
    <div className="relative">
      {shouldUseTextarea ? (
        <textarea
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={rows}
          className={cn(
            "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none align-top",
            className
          )}
          disabled={disabled}
          name={name}
          style={{ verticalAlign: 'top' }}
          {...props}
        />
      ) : (
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn(
            "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
            className
          )}
          disabled={disabled}
          name={name}
          {...props}
        />
      )}

      {showSuggestions && (
        <div
          ref={suggestionsRef}
          className="absolute z-[10030] w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto"
        >
          {filteredQuestions.map((question, index) => (
            <div
              key={question.id}
              className={cn(
                "px-3 py-2 cursor-pointer text-sm",
                index === selectedIndex
                  ? "bg-blue-50 text-blue-900"
                  : "text-gray-700 hover:bg-gray-50"
              )}
              onClick={() => handleSuggestionClick(question)}
            >
              <div className="font-medium">{`{{${question.text}}}`}</div>
              <div className="text-xs text-gray-500 truncate">Insert placeholder</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionInput; 